import { faPhoneVolume, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import { RingCentralQueryService } from '@supply-mover-2-0/core';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';

export interface RingCentralUserAuthModalProps {
	isOpen: boolean;
	onClose: () => void;
	onAuthComplete: () => void;
}

export function RingCentralUserAuthModal({
	isOpen,
	onClose,
	onAuthComplete,
}: RingCentralUserAuthModalProps): React.ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConnect = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const authUrlResponse = await RingCentralQueryService.getUserAuthUrl();

			if (authUrlResponse == null) {
				setError(t('Failed to get authorization URL. Please try again.'));
				setIsLoading(false);

				return;
			}

			// Calculate popup window position (centered)
			const width = 600;
			const height = 700;
			const left = window.screenX + (window.outerWidth - width) / 2;
			const top = window.screenY + (window.outerHeight - height) / 2;

			// Open OAuth popup
			const popup = window.open(
				authUrlResponse.url,
				'ringcentral_auth',
				`width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
			);

			if (popup == null) {
				setError(
					t(
						'Popup was blocked. Please allow popups for this site and try again.',
					),
				);
				setIsLoading(false);

				return;
			}

			// Poll for popup completion (fallback if postMessage doesn't work)
			const pollInterval = setInterval(() => {
				try {
					if (popup.closed) {
						clearInterval(pollInterval);
						setIsLoading(false);
						// The popup might have closed without completing - check auth status
						void RingCentralQueryService.getUserAuthStatus().then((status) => {
							if (status.authenticated) {
								onAuthComplete();
								onClose();
							}
						});
					}
				} catch {
					// Cross-origin errors are expected, ignore them
				}
			}, 500);
		} catch (err) {
			const caughtError = err as Error;
			const errorMessage =
				caughtError.message !== ''
					? caughtError.message
					: t('An unexpected error occurred.');

			setError(errorMessage);
			setIsLoading(false);
		}
	}, [t, onAuthComplete, onClose]);

	// Listen for OAuth callback message from popup
	// The callback page (RingCentralIntegration.tsx) handles the code exchange
	// and sends back a success/failure message
	useEffect(() => {
		const handleMessage = (event: MessageEvent): void => {
			// Only accept messages from same origin
			if (event.origin !== window.location.origin) {
				return;
			}

			const eventData = event.data as {
				type?: string;
				success?: boolean;
			} | null;

			if (eventData?.type === 'ringcentral_user_oauth_callback') {
				setIsLoading(false);

				if (eventData.success === true) {
					onAuthComplete();
					onClose();
				} else {
					setError(t('Failed to complete authentication. Please try again.'));
				}
			}
		};

		window.addEventListener('message', handleMessage);

		return (): void => {
			window.removeEventListener('message', handleMessage);
		};
	}, [t, onAuthComplete, onClose]);

	const onConnectClick = useCallback((): void => {
		void handleConnect();
	}, [handleConnect]);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={`w-full max-w-md transform rounded-lg ${colors.classes.bgPrimary} text-left align-middle shadow-xl transition-all`}
							>
								<div
									className={`px-6 py-4 border-b ${colors.classes.borderDefault}`}
								>
									<DialogTitle
										as="h3"
										className={`text-lg font-medium leading-6 ${colors.classes.textPrimary}`}
									>
										{t('Connect to RingCentral')}
									</DialogTitle>
								</div>

								<div className="px-6 py-6 space-y-4">
									<div className="flex justify-center">
										<div
											className={`w-16 h-16 rounded-full flex items-center justify-center ${colors.classes.badgeSuccess}`}
										>
											<FontAwesomeIcon
												icon={faPhoneVolume}
												className="text-white text-2xl"
											/>
										</div>
									</div>

									<p className={`text-center ${colors.classes.textSecondary}`}>
										{t(
											'To make calls using RingCentral, you need to connect your RingCentral account.',
										)}
									</p>

									<p
										className={`text-center text-sm ${colors.classes.textMuted}`}
									>
										{t(
											"You'll be redirected to RingCentral to authorize access. This allows us to place calls on your behalf.",
										)}
									</p>

									{error != null && (
										<div
											className={`p-3 rounded ${colors.classes.calloutDanger} ${colors.classes.textDanger}`}
										>
											{error}
										</div>
									)}
								</div>

								<div
									className={`flex justify-end gap-2 px-6 py-4 border-t ${colors.classes.borderDefault}`}
								>
									<Button
										variant="outline"
										onClick={onClose}
										disabled={isLoading}
									>
										{t('Cancel')}
									</Button>
									<Button
										variant="primary"
										onClick={onConnectClick}
										disabled={isLoading}
									>
										{isLoading ? (
											<>
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin mr-2"
												/>
												{t('Connecting...')}
											</>
										) : (
											t('Connect RingCentral')
										)}
									</Button>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
